// src/CompanyRegister/Step5.js
import React, { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { X } from "lucide-react";

const Step5 = ({ onNext, onBack, formData }) => {
  const [paginaWeb, setPaginaWeb] = useState(formData?.paginaWeb || "");
  const [telefonoEmpresa, setTelefonoEmpresa] = useState(
    formData?.telefonoEmpresa || ""
  );
  const [informacionContacto, setInformacionContacto] = useState(
    formData?.informacionContacto || []
  );
  const [redesSociales, setRedesSociales] = useState(
    formData?.redesSociales || []
  );

  const [newContact, setNewContact] = useState({
    nombre: "",
    telefono: "",
    email: "",
  });

  const [newSocial, setNewSocial] = useState({ red: "", url: "" });

  const [contactError, setContactError] = useState(""); // Error para información de contacto
  const [socialError, setSocialError] = useState(""); // Error para redes sociales
  const [telefonoError, setTelefonoError] = useState("");

  const handleNext = () => {
    if (paginaWeb && !/^(https?:\/\/)?[^\s/$.?#].[^\s]*$/i.test(paginaWeb)) {
      toast.warning(
        "Por favor ingresa una página web válida. Ejemplo: www.ejemplo.com o https://www.ejemplo.com"
      );
      return;
    }

    if (telefonoEmpresa && !/^\+?[\d\s()-]{9,15}$/.test(telefonoEmpresa)) {
      setTelefonoError(
        "El teléfono debe contener entre 9 y 15 dígitos y puede incluir caracteres como +, (), o espacios."
      );
      return;
    } else {
      setTelefonoError("");
    }
    // ⚠️ Validar si hay datos en los inputs pero no se han agregado
    if (
      (newContact.nombre || newContact.telefono || newContact.email) &&
      (!newContact.nombre || !newContact.telefono || !newContact.email)
    ) {
      toast.warning(
        "Para guardar la información de contacto, presiona 'Agregar'. De lo contrario, limpia los campos."
      );
      return;
    }

    if (newSocial.url) {
      toast.warning(
        "Para guardar la red social, presiona 'Agregar'. De lo contrario limpia el campo."
      );
      return;
    }

    onNext({
      paginaWeb,
      telefonoEmpresa,
      informacionContacto,
      redesSociales,
    });
  };

  const handleBack = () => {
    onBack({
      paginaWeb,
      telefonoEmpresa,
      informacionContacto,
      redesSociales,
    });
  };

  const handleAddContact = () => {
    setContactError(""); // Limpiar errores previos
    if (!newContact.nombre || !newContact.telefono || !newContact.email) {
      setContactError("Por favor, completa todos los campos de contacto.");
      return;
    }

    if (!/^\+?[\d\s()-]{9,15}$/.test(newContact.telefono)) {
      setContactError(
        "El teléfono debe contener entre 9 y 15 dígitos y puede incluir caracteres como +, (), o espacios."
      );
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newContact.email)) {
      setContactError(
        "El formato del correo no es válido. Ejemplo: correo@email.com"
      );
      return;
    }

    setInformacionContacto((prevContacts) => [...prevContacts, newContact]);

    // ✅ Mostrar datos del contacto en el alert
    toast.success(
      `✅ Contacto agregado correctamente\n\n📌 Nombre: ${newContact.nombre}\n📞 Teléfono: ${newContact.telefono}\n📧 Email: ${newContact.email}`
    );
    setNewContact({ nombre: "", telefono: "", email: "" });
    setContactError("");
  };

  const handleAddSocial = () => {
    setSocialError(""); // Limpiar errores previos

    if (redesSociales.length >= 5) {
      setSocialError("Solo puedes agregar un máximo de 5 redes sociales.");
      return;
    }

    if (!newSocial.url) {
      setSocialError("Por favor, proporciona una URL válida.");
      return;
    }

    // ✅ Mostrar alerta con la información de la red social agregada
    toast.success(`✅ Red social agregada correctamente\n\n🔗 URL: ${newSocial.url}`);

    setRedesSociales((prevSocials) => [...prevSocials, { url: newSocial.url }]);
    setNewSocial({ url: "" });
    setSocialError("");
  };

  const handleRemoveContact = (index) => {
    setInformacionContacto((prevContacts) =>
      prevContacts.filter((_, i) => i !== index)
    );
  };

  const handleRemoveSocial = (index) => {
    setRedesSociales((prevSocials) =>
      prevSocials.filter((_, i) => i !== index)
    );
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <h3 className="text-xl font-semibold text-foreground">Paso 5: Contacto (opcional)</h3>
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          {/* Página Web */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Página Web de la empresa (opcional)
            </label>
            <Input
              type="url"
              value={paginaWeb}
              onChange={(e) => setPaginaWeb(e.target.value)}
              placeholder="https://www.empresa.com"
              maxLength={2000}
            />
          </div>

          {/* Teléfono de la Empresa */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Teléfono de la empresa (opcional)
            </label>
            <Input
              type="text"
              value={telefonoEmpresa}
              onChange={(e) => setTelefonoEmpresa(e.target.value)}
              placeholder="+51 987 654 321"
              maxLength={15}
            />
            {telefonoError && <p className="mt-1 text-sm text-destructive">{telefonoError}</p>}
          </div>

          {/* Información de Contacto */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Agrega información de contacto de personas que trabajen en la
              empresa (opcional y max 5 personas)
            </label>
            <div className="grid gap-2 sm:grid-cols-3">
              <Input
                type="text"
                placeholder="Nombre"
                value={newContact.nombre}
                maxLength={50}
                onChange={(e) =>
                  setNewContact({ ...newContact, nombre: e.target.value })
                }
              />
              <Input
                type="text"
                placeholder="Celular"
                value={newContact.telefono}
                maxLength={15}
                onChange={(e) =>
                  setNewContact({ ...newContact, telefono: e.target.value })
                }
              />
              <Input
                type="email"
                placeholder="email@gmail.com"
                value={newContact.email}
                maxLength={60}
                onChange={(e) =>
                  setNewContact({ ...newContact, email: e.target.value })
                }
              />
            </div>
            <Button type="button" variant="outline" className="mt-2" onClick={handleAddContact}>
              Agregar
            </Button>
            {informacionContacto.length > 0 && (
              <ul className="mt-2 space-y-1">
                {informacionContacto.map((contact, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    {`${contact.nombre} - Cel: ${contact.telefono}  - Email: ${contact.email}`}
                    <button type="button" onClick={() => handleRemoveContact(index)} className="hover:text-destructive">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {contactError && <p className="mt-1 text-sm text-destructive">{contactError}</p>}
          </div>

          {/* Redes Sociales */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              Agrega paginas de redes sociales (opcional y max 5)
            </label>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="Ejemplo: https://www.facebook.com/miPerfil"
                value={newSocial.url}
                maxLength={2000}
                onChange={(e) =>
                  setNewSocial({ ...newSocial, url: e.target.value })
                }
              />
            </div>
            <Button type="button" variant="outline" className="mt-2" onClick={handleAddSocial}>
              Agregar
            </Button>
            {redesSociales.length > 0 && (
              <ul className="mt-2 space-y-1">
                {redesSociales.map((social, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                    {social.url}
                    <button type="button" onClick={() => handleRemoveSocial(index)} className="hover:text-destructive">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {socialError && <p className="mt-1 text-sm text-destructive">{socialError}</p>}
          </div>

          {/* Navegación */}
          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={handleBack}>
              Retroceder
            </Button>
            <Button type="button" onClick={handleNext}>
              Siguiente
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default Step5;
